import gsap from 'gsap'
import Texture2D from 'nanogl/texture-2d'
import Animation from 'nanogl-gltf/lib/elements/Animation'
import MaterialOverrideExtension from 'nanogl-gltf/lib/extensions/MaterialOverrideExtension'
import Gltf from 'nanogl-gltf/lib/Gltf'
import MeshRenderer from 'nanogl-gltf/lib/renderer/MeshRenderer'
import GltfTypes from 'nanogl-gltf/lib/types/GltfTypes'
import UnlitPass from 'nanogl-pbr/UnlitPass'

import { MusicAudio } from '@/config/AudioConfig'
import { HexToVec3 } from '@/webgl/core/Color'
import Deferred from '@/webgl/core/Deferred'
import { RenderContext } from '@/webgl/core/RenderContext'
import Signal from '@/webgl/core/Signal'
import { clamp } from '@/webgl/math'
import AssetDatabase from '@/webgl/resources/AssetDatabase'
import GltfResource from '@/webgl/resources/GltfResource'
import LanesMergeAnimation from '@/webgl/runner/animations/LanesMergeAnimation'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import PortalSpiralChunk from '@/webgl/runner/rendering/chunks/PortalSpiralChunk'
import { backgroundLayer } from '@/webgl/runner/rendering/RenderingUtils'
import ScreenSpiralRenderer from '@/webgl/runner/rendering/ScreenSpiralRenderer'
import { ScenarioSetup } from '@/webgl/runner/scenarios/ScenarioSetup.types'
import MusicSystem from '@/webgl/runner/systems/MusicSystem'
import ScrollSystem from '@/webgl/runner/systems/ScrollSystem'
import { IGameSegment } from '@/webgl/runner/systems/SequencingSystem'

export default class SpiderSegment implements IGameSegment {
  id: string
  onComplete: Signal<void> = new Signal<void>()
  world: GameWorld
  renderer: IRunnerRenderer

  laneMerge: LanesMergeAnimation

  spiralTex: Texture2D
  spiralEye: MeshRenderer
  eyeGltf: Gltf
  gltf: Gltf
  animation: Animation
  animationTime: number
  animationDuration: number
  screenMask: ScreenSpiralRenderer
  gltfRender: boolean
  stopped: boolean = false
  entered: boolean = false

  constructor(id: string = 'spider') {
    this.id = id
  }

  init(world: GameWorld, renderer: IRunnerRenderer, setup: ScenarioSetup): void {
    this.world = world
    this.renderer = renderer
    this.laneMerge = new LanesMergeAnimation(renderer.lanes)
    this.screenMask = new ScreenSpiralRenderer(renderer.gl)
    this.screenMask.rotate = false
    this.entered = false
  }

  async load(): Promise<void> {
    // Background
    const overrides = new MaterialOverrideExtension()
    const spiralTex = await AssetDatabase.getTexture('francesca_1/spider_spiral.jpg', this.renderer.gl, {
      wrap: 'clamp',
    }).load()

    const chunk = new PortalSpiralChunk(this.world.time)
    chunk.setTexture(spiralTex)
    chunk.visibility.set(0, 0, 1)

    HexToVec3(0x024860, chunk.color1)
    HexToVec3(0x06748d, chunk.color2)

    overrides.overridePass('glowing_eye', (ctx, m) => {
      const pass = m.getPass('color').pass as UnlitPass
      backgroundLayer(this.renderer.gl, pass)
      pass.inputs.add(chunk)
      return pass
    })

    this.eyeGltf = await new GltfResource('common/glowing_eye.glb', this.renderer.gl, {
      extensions: [overrides],
    }).load()

    this.spiralEye = this.eyeGltf.getElementByName(GltfTypes.NODE, 'glowing_eye').renderable

    // Web
    this.gltf = await new GltfResource('francesca_1/spider_web.glb', this.renderer.gl).load()

    const settings = this.world.settings

    this.animation = this.gltf.animations[0]
    this.animationTime = 0
    this.animationDuration = this.animation.duration
    this.gltf.root.z = -settings.LANE_LENGTH
    this.world.root.add(this.gltf.root)

    const tex = await AssetDatabase.getTexture('francesca_1/spider_mask.jpg', this.renderer.gl, {
      wrap: 'clamp',
    }).load()
    this.screenMask.setTexture(tex)
  }

  async start(): Promise<void> {
    this.world.getTrackingSystem().trackImpression({
      unitName: `francesca_simulation_0`,
      unitVariation: `scenario_auto_obstacle_spider`,
      unitLocation: 'tyce',
    })

    this.stopped = false
    this.world.background.spawnableItems = []
    this.world.systems.get<ScrollSystem>(ScrollSystem).enable()
    this.animationTime = 0

    const settings = this.world.settings
    this.gltfRender = true

    this.gltf.root.z = -settings.LANE_LENGTH * 0.9

    this.world.root.add(this.gltf.root)

    const player = this.world.player
    player.direction = 0
    player.lane = 0
    player.locked = true
    gsap.to(player.node, { x: 0, duration: 1.0 })

    this.laneMerge.init()
    await this.laneMerge.play()
    // if (this.stopped) return;

    // await Delay((this.animationDuration - this.animationTime) * 1000);
    // if (this.stopped) return;
  }

  async firstFlash(): Promise<void> {
    const defer = new Deferred<void>()
    gsap.to(this.screenMask, {
      reveal: 0.5,
      duration: 0.7,
      ease: 'linear',
      onComplete: () => defer.resolve(),
    })
    return defer.promise
  }

  async releaseFlash(): Promise<void> {
    const defer = new Deferred<void>()
    gsap.to(this.screenMask, {
      reveal: 1.0,
      duration: 1.0,
      ease: 'linear',
      onComplete: () => defer.resolve(),
    })
    return defer.promise
  }

  async enterSpiderWorld() {
    this.entered = true

    await this.firstFlash()
    if (this.stopped) return

    this.world.systems.get<MusicSystem>(MusicSystem).play(MusicAudio.Spiders)

    this.renderer.eye.setScale(1.8)
    this.renderer.glRenderer.setClearColor(0x024860)
    this.renderer.background.updateEye(this.spiralEye)
    this.renderer.background.setColor(0x06748d)
    this.renderer.player.setColors(0x06748d, 0x06748d, 0x06748d)
    this.renderer.lanes.setMaskColor(0x06748d)
    this.renderer.lanes.setColor(0x024860)
    this.renderer.smoke.setColor(0x06748d)
    this.laneMerge.cleanup()
    this.gltfRender = false
    this.world.state.alterJauge(1)

    this.world.background.spawnableItems = [
      'spider_books',
      'spider_calculator',
      'spider_pen_pot',
      'spider_pencil_case',
      'spider_sharpener',
    ]
    this.world.background.activeItems = []

    await this.releaseFlash()

    this.onComplete.emit()
  }

  update(time: TimeEntity): void {
    const settings = this.world.settings
    const paused = settings.scrollSpeed == 0
    const factor = settings.scrollSpeed == 0 ? 0 : 1

    const z = this.gltf.root.z + time.unscaledDeltaTime * 1.15 * factor
    this.gltf.root.z = Math.min(z, -settings.GRAB_THRESHOLD - 0.2)

    this.animation.evaluate(this.animationTime)
    this.animationTime += time.unscaledDeltaTime * factor
    this.animationTime = clamp(this.animationTime, 0, this.animationDuration)

    if (this.animationTime == this.animationDuration && !this.entered) {
      this.enterSpiderWorld()
    }
  }

  render(context: RenderContext) {
    if (this.gltfRender) {
      const renderables = this.gltf.renderables.sort((a, b) => a.node._wposition[2] - b.node._wposition[2])
      for (const renderable of renderables) {
        renderable.render(context.gl, context.camera, context.mask, context.pass, context.glConfig)
      }
    }

    this.screenMask.render(context)
  }

  stop(): void {
    this.stopped = true
    this.world.player.locked = false
    this.world.root.remove(this.gltf.root)
  }
}
